import React from "react";
import { UserCircle2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CURRENCIES } from "@/lib/labelSizes";
import { FieldLabel, SectionLabel } from "./FormPrimitives";

// Currency badge shown inside amount inputs (replaces dollar icon)
const CcyBadge = ({ ccy }) => (
  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold tracking-wider text-zinc-500 font-mono pointer-events-none">
    {ccy || "AED"}
  </span>
);

export const SalesCard = ({ form, setField, salesPersons, users = [] }) => (
  <Card className="border-zinc-200 shadow-sm">
    <div className="p-6 md:p-8">
      <SectionLabel hint="Sales Person required">Sales</SectionLabel>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FieldLabel htmlFor="sales_person" icon={UserCircle2}>
            Sales Person <span className="text-red-600">*</span>
          </FieldLabel>
          <Select
            value={form.sales_person || ""}
            onValueChange={(v) => setField("sales_person", v)}
          >
            <SelectTrigger className="h-11" data-testid="select-sales-person-trigger">
              <SelectValue placeholder={users.length ? "Select a sales person" : "No users configured"} />
            </SelectTrigger>
            <SelectContent>
              {users.length === 0 ? (
                <div className="px-3 py-2 text-xs text-zinc-500">
                  Admin needs to add users first.
                </div>
              ) : (
                users.map((u) => (
                  <SelectItem
                    key={u.id}
                    value={u.name || u.email}
                    data-testid={`sales-person-${u.id}`}
                  >
                    {u.name || u.email}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="job_coordinator_user_id" icon={UserCircle2}>
            Job Coordinator <span className="text-red-600">*</span>
          </FieldLabel>
          <Select
            value={form.job_coordinator_user_id || ""}
            onValueChange={(v) => setField("job_coordinator_user_id", v)}
          >
            <SelectTrigger className="h-11" data-testid="select-job-coordinator-trigger">
              <SelectValue placeholder={users.length ? "Select a job coordinator" : "No users configured"} />
            </SelectTrigger>
            <SelectContent>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`job-coordinator-${u.id}`}>
                  {u.name || u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="account_of_user_id" icon={UserCircle2}>Account of (User)</FieldLabel>
          <Select
            value={form.account_of_user_id || "none"}
            onValueChange={(v) => setField("account_of_user_id", v === "none" ? "" : v)}
          >
            <SelectTrigger className="h-11" data-testid="select-account-of-trigger">
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned (only me)</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`account-of-${u.id}`}>
                  {u.name || u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="sales_currency">Currency</FieldLabel>
          <Select
            value={form.sales_currency}
            onValueChange={(v) => setField("sales_currency", v)}
          >
            <SelectTrigger className="h-11" data-testid="select-currency-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`currency-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel htmlFor="sales_amount">Sales Amount ({form.sales_currency || "AED"})</FieldLabel>
          <div className="relative">
            <CcyBadge ccy={form.sales_currency} />
            <Input
              id="sales_amount"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00 (optional)"
              value={form.sales_amount}
              onChange={(e) => setField("sales_amount", e.target.value)}
              className="h-11 font-mono pl-14"
              data-testid="input-sales-amount"
            />
          </div>
        </div>
        <div>
          <FieldLabel htmlFor="cost">Cost ({form.sales_currency || "AED"})</FieldLabel>
          <div className="relative">
            <CcyBadge ccy={form.sales_currency} />
            <Input
              id="cost"
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00 (optional)"
              value={form.cost}
              onChange={(e) => setField("cost", e.target.value)}
              className="h-11 font-mono pl-14"
              data-testid="input-cost"
            />
          </div>
        </div>
        <div className="md:col-span-2">
          <FieldLabel htmlFor="margin">Margin ({form.sales_currency || "AED"})</FieldLabel>
          <div className="relative">
            <CcyBadge ccy={form.sales_currency} />
            <Input
              id="margin"
              type="number"
              step="0.01"
              placeholder={
                form.sales_amount !== "" && form.cost !== ""
                  ? `Auto: ${(Number(form.sales_amount) - Number(form.cost)).toFixed(2)} (override or leave blank)`
                  : "0.00 (optional)"
              }
              value={form.margin}
              onChange={(e) => setField("margin", e.target.value)}
              className="h-11 font-mono pl-14"
              data-testid="input-margin"
            />
          </div>
          {form.sales_amount !== "" && form.cost !== "" && form.margin === "" && (
            <p className="text-[11px] text-zinc-500 mt-1 font-mono">
              Will be auto-calculated as Sales − Cost when saved blank.
            </p>
          )}
        </div>
      </div>
    </div>
  </Card>
);
